/**
 * Pure supercruise physics — speed-ceiling math only. No Three.js, no DOM,
 * no Math.random, no Date.now. Exported from sim-core so the client can import
 * the constants + pure fn AND unit tests can cover them directly.
 */

/** Maximum supercruise speed in open space (km/s). */
export const OPEN_SPACE_MAX = 2_000_000;

/** Minimum supercruise speed floor (km/s) — lets you inch right up to a body. */
export const NEAR_BODY_MIN = 20;

/**
 * Arrival-time constant (seconds). cruiseCeilingKmS = distance / ARRIVE_TIME,
 * so flight time to any target at ceiling throttle ≈ ARRIVE_TIME seconds.
 */
export const ARRIVE_TIME = 8;

/**
 * PURE ceiling function: maximum supercruise speed (km/s) given surface
 * clearance to the nearest body and/or distance to the active nav-target.
 *
 * - Body term: `min(OPEN_SPACE_MAX, max(NEAR_BODY_MIN, clearance / ARRIVE_TIME))`
 *   — bleeds speed as you close on a planet so you don't overshoot.
 * - Target term: `max(NEAR_BODY_MIN, targetDist / ARRIVE_TIME)`
 *   — bleeds speed as you close on a locked signal/body/station so arrival
 *   time stays roughly constant regardless of initial distance.
 * - When both are present the stricter (lower) ceiling wins.
 * - When neither is present the constant OPEN_SPACE_MAX is returned.
 *
 * @param surfaceClearanceKm  km to nearest body surface; null = deep space
 * @param targetDistanceKm    km to locked nav-target; null = no lock
 */
export const cruiseCeilingKmS = (
  surfaceClearanceKm: number | null,
  targetDistanceKm: number | null,
): number => {
  const bodyCeiling = Math.min(
    OPEN_SPACE_MAX,
    Math.max(NEAR_BODY_MIN, (surfaceClearanceKm ?? OPEN_SPACE_MAX * ARRIVE_TIME) / ARRIVE_TIME),
  );
  if (targetDistanceKm === null) return bodyCeiling;
  const targetCeiling = Math.max(NEAR_BODY_MIN, targetDistanceKm / ARRIVE_TIME);
  return Math.min(bodyCeiling, targetCeiling);
};
